if (!customElements.get('product-grid-card')) {
  customElements.define(
    'product-grid-card',
    class ProductGridCard extends HTMLElement {
      connectedCallback() {
        this.variants = JSON.parse(
          this.querySelector('[data-variant-data]')?.textContent || '[]',
        );
        this.addEventListener('click', this.handleClick);
        this.fitBadges();

        if ('ResizeObserver' in window) {
          this.badgeResizeObserver = new ResizeObserver(() => this.fitBadges());
          this.badgeResizeObserver.observe(this);
        } else {
          window.addEventListener('resize', this.fitBadges);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.badgeResizeObserver?.disconnect();
        window.removeEventListener('resize', this.fitBadges);
      }

      handleClick = (event) => {
        const swatch = event.target.closest('[data-swatch]');
        if (!swatch || swatch.disabled) return;

        const variant = this.variants.find(
          ({id}) => String(id) === swatch.dataset.variantId,
        );
        if (!variant) return;

        this.selectVariant(variant, swatch);
      };

      selectVariant(variant, swatch) {
        this.querySelectorAll('[data-swatch]').forEach((item) => {
          item.setAttribute('aria-pressed', String(item === swatch));
        });

        const input = this.querySelector('[data-variant-input]');
        if (input) {
          input.value = variant.id;
          input.disabled = !variant.available;
        }

        this.querySelectorAll('[data-product-link]').forEach((link) => {
          link.href = variant.url;
        });

        const image = this.querySelector('[data-product-image]');
        if (image && variant.image?.src) {
          image.src = variant.image.src;
          image.srcset = variant.image.srcset || '';
          image.alt = variant.image.alt || '';
        }

        const price = this.querySelector('[data-price]');
        if (price) price.textContent = variant.price;

        const compare = this.querySelector('[data-compare-price]');
        if (compare) {
          compare.textContent = variant.compareAtPrice || '';
          compare.hidden = !variant.compareAtPrice;
        }

        const action = this.querySelector('[data-product-action]');
        if (action instanceof HTMLButtonElement) {
          action.disabled = !variant.available;
          action.dataset.available = String(variant.available);
          const label = action.querySelector('[data-action-label]');
          if (label) {
            label.textContent = variant.available
              ? action.dataset.addLabel || 'Quick add'
              : action.dataset.soldOutLabel || 'Sold out';
          }
        }

        const status = this.querySelector('[data-variant-status]');
        if (status) {
          status.textContent = `${variant.price}. ${
            variant.available ? 'Available' : 'Sold out'
          }`;
        }
      }

      fitBadges = () => {
        const badges = this.querySelector('[data-product-grid-badges]');
        const firstBadge = badges?.querySelector('.product-grid-card__badge');
        if (!badges || !firstBadge || badges.clientWidth <= 0) return;

        badges.style.removeProperty('--product-grid-badge-font-size');

        const maxSize = parseFloat(getComputedStyle(firstBadge).fontSize);
        const minSize = Math.max(7, maxSize * 0.52);
        let nextSize = maxSize;

        while (badges.scrollWidth > badges.clientWidth && nextSize > minSize) {
          nextSize -= 0.25;
          badges.style.setProperty('--product-grid-badge-font-size', `${nextSize}px`);
        }
      };
    },
  );
}

if (!customElements.get('product-grid-form')) {
  customElements.define(
    'product-grid-form',
    class ProductGridForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.form?.addEventListener('submit', this.handleSubmit);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.handleSubmit);
      }

      handleSubmit = async (event) => {
        event.preventDefault();
        const button = this.querySelector('[type="submit"]');
        if (!this.form || button?.disabled) return;

        button?.setAttribute('aria-disabled', 'true');
        button?.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          const formData = new FormData(this.form);
          const cart = document.querySelector('cart-drawer, cart-notification');
          const sectionsToRender = cart?.getSectionsToRender?.() || [];

          if (sectionsToRender.length) {
            sectionsToRender.forEach(({id, selector}) => {
              if (selector || document.getElementById(id)) return;

              const placeholder = document.createElement('div');
              placeholder.id = id;
              placeholder.hidden = true;
              document.body.appendChild(placeholder);
            });
            formData.append(
              'sections',
              sectionsToRender.map(({id}) => id),
            );
            formData.append('sections_url', window.location.pathname);
            cart.setActiveElement?.(document.activeElement);
          }

          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || 'Add to cart failed');

          if (cart?.renderContents) {
            cart.renderContents(result);
            cart.classList.remove('is-empty');
          } else {
            document.dispatchEvent(
              new CustomEvent('cart:item-added', {detail: result, bubbles: true}),
            );
          }
        } catch (error) {
          console.error(error);
        } finally {
          button?.removeAttribute('aria-disabled');
          button?.classList.remove('loading');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      };
    },
  );
}

if (!customElements.get('product-grid-section')) {
  customElements.define(
    'product-grid-section',
    class ProductGridSection extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', this.handleChange);
        this.addEventListener('click', this.handleClick);
        this.addEventListener('submit', this.handleSubmit);
        window.addEventListener('popstate', this.handlePopState);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.handleChange);
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('submit', this.handleSubmit);
        window.removeEventListener('popstate', this.handlePopState);
        this.controller?.abort();
      }

      handleChange = (event) => {
        const demoFilter = event.target.closest('[data-product-grid-demo-filter]');
        if (demoFilter) {
          if (demoFilter.closest('.product-grid-facets--mobile')) return;
          this.applyDemoFilters();
          return;
        }

        const form = event.target.closest(
          '[data-product-grid-filter-form], [data-product-grid-sort-form]',
        );
        if (!form) return;
        if (form.classList.contains('product-grid-facets--mobile')) return;
        this.render(new URLSearchParams(new FormData(form)));
      };

      handleClick = (event) => {
        const closeButton = event.target.closest(
          '[data-product-grid-mobile-filter-close]',
        );
        if (closeButton) {
          const drawer = closeButton.closest('[data-product-grid-mobile-filter]');
          if (drawer) drawer.open = false;
          document.documentElement.classList.remove('product-grid-mobile-filter-open');
          return;
        }

        const trigger = event.target.closest('.product-grid-section__mobile-trigger');
        if (!trigger) return;
        const drawer = trigger.closest('[data-product-grid-mobile-filter]');
        if (!drawer) return;

        requestAnimationFrame(() => {
          document.documentElement.classList.toggle(
            'product-grid-mobile-filter-open',
            drawer.open,
          );
          if (drawer.open) {
            drawer
              .querySelectorAll('.product-grid-facets--mobile details[open]')
              .forEach((detail) => {
                detail.open = false;
              });
          }
        });
      };

      handleSubmit = (event) => {
        const form = event.target.closest(
          '[data-product-grid-filter-form].product-grid-facets--mobile',
        );
        if (!form) return;

        event.preventDefault();
        const drawer = form.closest('[data-product-grid-mobile-filter]');
        if (drawer) drawer.open = false;
        document.documentElement.classList.remove('product-grid-mobile-filter-open');

        if (form.querySelector('[data-product-grid-demo-filter]')) {
          this.applyDemoFilters();
          return;
        }

        this.render(new URLSearchParams(new FormData(form)));
      };

      handlePopState = () => {
        this.render(new URLSearchParams(window.location.search), false);
      };

      applyDemoFilters() {
        const selectedByGroup = new Map();
        this.querySelectorAll('[data-product-grid-demo-filter]:checked').forEach(
          (input) => {
            const group = input.dataset.demoFilterGroup || 'default';
            const values = selectedByGroup.get(group) || [];
            values.push(input.value);
            selectedByGroup.set(group, values);
          },
        );

        const cards = this.querySelectorAll('product-grid-card');
        cards.forEach((card) => {
          const tags = (card.dataset.filterTags || '')
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean);

          const isVisible = [...selectedByGroup.values()].every((groupTags) =>
            groupTags.some((tag) => tags.includes(tag)),
          );
          const item = card.closest('li');
          if (item) item.hidden = selectedByGroup.size > 0 && !isVisible;
        });
      }

      async render(searchParams, updateHistory = true) {
        this.controller?.abort();
        this.controller = new AbortController();

        const url = new URL(window.location.href);
        url.search = searchParams.toString();
        const sectionURL = new URL(url);
        sectionURL.searchParams.set('section_id', this.dataset.sectionId);
        this.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(sectionURL, {signal: this.controller.signal});
          if (!response.ok) throw new Error(`Product grid request failed: ${response.status}`);
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const nextRoot = parsed.querySelector('[data-product-grid-root]');
          const currentRoot = this.querySelector('[data-product-grid-root]');
          if (!nextRoot || !currentRoot) throw new Error('Product grid response is incomplete');

          currentRoot.replaceWith(nextRoot);
          if (updateHistory) history.pushState({}, '', url);
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        } finally {
          this.removeAttribute('aria-busy');
        }
      }
    },
  );
}

